import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import { ensurePayrocHostedFieldsLoaded } from '../services/payrocHostedFieldsLoader';
import { mountPayrocAchHostedFields, mountPayrocCardHostedFields } from '../services/payrocHostedFields';

export function PayrocCardHostedSection({
  sessionToken,
  onSubmissionSuccess,
  onError,
}) {
  const teardownRef = useRef(null);
  const successRef = useRef(onSubmissionSuccess);
  const errorRef = useRef(onError);
  successRef.current = onSubmissionSuccess;
  errorRef.current = onError;

  useEffect(() => {
    if (!sessionToken) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        await ensurePayrocHostedFieldsLoaded();
        if (cancelled) return;
        const teardown = mountPayrocCardHostedFields({
          sessionToken,
          onSubmissionSuccess: (payload) => successRef.current(payload),
          onError: (err) => errorRef.current && errorRef.current(err),
        });
        if (!cancelled) {
          teardownRef.current = teardown;
        } else if (typeof teardown.destroy === 'function') {
          teardown.destroy();
        }
      } catch (e) {
        if (!cancelled && errorRef.current) {
          errorRef.current({ type: 'init', message: e.message });
        }
      }
    })();

    return () => {
      cancelled = true;
      const teardown = teardownRef.current;
      teardownRef.current = null;
      if (teardown && typeof teardown.destroy === 'function') {
        teardown.destroy();
      }
    };
  }, [sessionToken]);

  return (
    <div className="rhinopay-payroc-card payroc-form">
      <label htmlFor="rhinopay-payroc-cardholder">Cardholder Name</label>
      <div className="payroc-card-holder-name" id="rhinopay-payroc-cardholder" />
      <div className="payroc-card-holder-name-error error-message" />
      <label htmlFor="rhinopay-payroc-number">Card Number</label>
      <div className="payroc-card-number" id="rhinopay-payroc-number" />
      <div className="payroc-card-number-error error-message" />
      <div className="cols-2">
        <div className="col">
          <label htmlFor="rhinopay-payroc-expiry">Expires (MM/YY)</label>
          <div className="payroc-card-expiry" id="rhinopay-payroc-expiry" />
          <div className="payroc-card-expiry-error error-message" />
        </div>
        <div className="payroc-card-cvv-wrapper">
          <div className="col">
            <label htmlFor="rhinopay-payroc-cvv">CVV</label>
            <div className="payroc-card-cvv" id="rhinopay-payroc-cvv" />
            <div className="payroc-card-cvv-error error-message" />
          </div>
        </div>
      </div>
      <div className="payroc-card-submit submit-button" />
    </div>
  );
}

PayrocCardHostedSection.propTypes = {
  sessionToken: PropTypes.string,
  onSubmissionSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

PayrocCardHostedSection.defaultProps = {
  sessionToken: null,
  onError: undefined,
};

export function PayrocAchHostedSection({
  sessionToken,
  onSubmissionSuccess,
  onError,
}) {
  const teardownRef = useRef(null);
  const successRef = useRef(onSubmissionSuccess);
  const errorRef = useRef(onError);
  successRef.current = onSubmissionSuccess;
  errorRef.current = onError;

  useEffect(() => {
    if (!sessionToken) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        await ensurePayrocHostedFieldsLoaded();
        if (cancelled) return;
        const teardown = mountPayrocAchHostedFields({
          sessionToken,
          onSubmissionSuccess: (payload) => successRef.current(payload),
          onError: (err) => errorRef.current && errorRef.current(err),
        });
        if (!cancelled) {
          teardownRef.current = teardown;
        } else if (typeof teardown.destroy === 'function') {
          teardown.destroy();
        }
      } catch (e) {
        if (!cancelled && errorRef.current) {
          errorRef.current({ type: 'init', message: e.message });
        }
      }
    })();

    return () => {
      cancelled = true;
      const teardown = teardownRef.current;
      teardownRef.current = null;
      if (teardown && typeof teardown.destroy === 'function') {
        teardown.destroy();
      }
    };
  }, [sessionToken]);

  return (
    <div className="rhinopay-payroc-ach payroc-form">
      <label htmlFor="rhinopay-payroc-ach-name">Accountholder Name</label>
      <div className="payroc-ach-account-holder" id="rhinopay-payroc-ach-name" />
      <div className="payroc-ach-account-holder-error error-message" />
      <label htmlFor="rhinopay-payroc-ach-type">Account Type</label>
      <div className="payroc-ach-account-type" id="rhinopay-payroc-ach-type" />
      <div className="payroc-ach-account-type-error error-message" />
      <label htmlFor="rhinopay-payroc-ach-acct">Account Number</label>
      <div className="payroc-ach-account-number" id="rhinopay-payroc-ach-acct" />
      <div className="payroc-ach-account-number-error error-message" />
      <label htmlFor="rhinopay-payroc-ach-route">Routing Number</label>
      <div className="payroc-ach-routing-number" id="rhinopay-payroc-ach-route" />
      <div className="payroc-ach-routing-number-error error-message" />
      <div className="payroc-ach-submit submit-button" />
    </div>
  );
}

PayrocAchHostedSection.propTypes = {
  sessionToken: PropTypes.string,
  onSubmissionSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

PayrocAchHostedSection.defaultProps = {
  sessionToken: null,
  onError: undefined,
};
